import { CaretDownIcon } from '@phosphor-icons/react'
import * as Tabs from '@radix-ui/react-tabs'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import invariant from 'tiny-invariant'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'
import { Breadcrumb } from '../../components/breadcrumb.tsx'
import { ErrorBoundary } from '../../components/error-boundaries/error-boundary'
import { NewerScanBanner } from '../../components/newer-scan-banner/newer-scan-banner.tsx'

import { RepositoryActionMenu } from '../../components/menus/repository-action-menu'
import {
  RepositoryActionMenuStoreProvider,
  useRepositoryActionMenuStore,
} from '../../components/menus/repository-action-menu.store'
import { ToolSelector } from '../../components/menus/tool-selector'
import { useGetToolsForRepository } from '../../components/menus/tool-selector.hooks'
import { RepositoryActionOverlays } from '../../components/repository-action-overlays'
import { RepositoryHeader } from '../../components/repository-header'
import { Spinner } from '../../components/spinner'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import { useToastService } from '../../services/toast-service.tsx'
import { ANTARES_DEMO_TOOL_NAME, isAntaresDemoRepositoryId } from '../../utils/antares-demo-identity.ts'
import {
  useGetRepository,
  useGetRepositoryIntegrations,
  useGetScans,
  useGetWorkflows,
  usePostUploadScan,
} from '../../utils/api-client/api-hooks'
import { Repository, ToolSchema, type Tool } from '../../utils/api-client/api-schemas.ts'
import { convertIntegrationTypeToTool, getToolName } from '../../utils/repository-utils.tsx'
import * as scansOverviewPageStyles from '../scans-overview-page.css'
import { ExploitChainsTab } from './exploit-chains-tab.tsx'
import { FindingOverlaysTab } from './finding-overlays-tab.tsx'
import { FindingsTabWithProviders } from './findings-tab.tsx'
import * as styles from './repository-details-page.css.ts'
import { patchSearchParams } from './scans-list.tsx'
import { ScansTab } from './scans-tab.tsx'
import { SURFACE_SECTION_PARAM } from './threat-model-navigation.ts'
import { ThreatModelTab } from './threat-model-tab.tsx'

export type RepositoryDetailsPageTab = 'findings' | 'scans' | 'threat-model' | 'exploit-chains' | 'finding-overlays'

const RepositoryDetailsPageTabSchema = z.enum([
  'findings',
  'scans',
  'threat-model',
  'exploit-chains',
  'finding-overlays',
])

const parseTab = (raw: string | null): RepositoryDetailsPageTab =>
  RepositoryDetailsPageTabSchema.safeParse(raw).data ?? 'findings'

/**
 * The tabs whose content is scoped by the selected tool.
 *
 * Findings and Scans pass `?tool=` into their queries. The threat model is built for the repository as
 * a whole, and everything derived from it — exploit chains, scan prioritization — spans every tool's
 * findings at once; all three query by `repositoryId` alone. Offering a live tool selector on those
 * three implies a filter that does not exist.
 */
const TOOL_SCOPED_TABS: readonly RepositoryDetailsPageTab[] = ['findings', 'scans']

const REPOSITORY_WIDE_REASON =
  'Threat models and the views derived from them are not bound to a single tool, but rather cover ' +
  'the entire repository across all tools.'

export const RepositoryDetailsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'))

  const setTab = useCallback(
    (value: RepositoryDetailsPageTab) => {
      setSearchParams(prev =>
        patchSearchParams(prev, {
          tab: value === 'findings' ? null : value,
          // The section anchor is a deep-link into one threat-model inventory section. Leaving the
          // threat model for another top-level tab ends that arrival, so drop it — otherwise coming
          // back re-scrolls and re-highlights a section the reader has moved on from.
          [SURFACE_SECTION_PARAM]: null,
        })
      )
    },
    [setSearchParams]
  )

  useSelectToolOnLoad()

  return (
    <RepositoryActionMenuStoreProvider>
      <div style={{ display: 'flex', flexDirection: 'column', margin: '24px 0', gap: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', margin: '0 72px' }}>
          <RepositoryDetailsPageBreadcrumb />
          {tab === 'findings' && <RepositoryDetailsPageNewerScanBanner />}
        </div>

        <HeaderSection />

        <Tabs.Root value={tab} onValueChange={value => setTab(value as RepositoryDetailsPageTab)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
            <Tabs.List className={styles.tabList} aria-label="Repository sections">
              <div style={{ display: 'flex', width: '100%', gap: '48px' }}>
                <ToolSelectorWrapper />
                <div>
                  <Tabs.Trigger value="findings" className={scansOverviewPageStyles.tab}>
                    Findings
                  </Tabs.Trigger>
                  <Tabs.Trigger value="scans" className={scansOverviewPageStyles.tab}>
                    Scans
                  </Tabs.Trigger>
                  <Tabs.Trigger value="threat-model" className={scansOverviewPageStyles.tab}>
                    Threat model
                  </Tabs.Trigger>
                  <Tabs.Trigger value="exploit-chains" className={scansOverviewPageStyles.tab}>
                    Exploit chains
                  </Tabs.Trigger>
                  <Tabs.Trigger value="finding-overlays" className={scansOverviewPageStyles.tab}>
                    Scan prioritization
                  </Tabs.Trigger>
                </div>
              </div>
            </Tabs.List>
            <div style={{ margin: '0 72px' }}>
              <Tabs.Content value="findings" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Failed to load findings">
                  <FindingsTabWithProviders />
                </ErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="scans" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Failed to load scans">
                  <ScansTab />
                </ErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="threat-model" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Failed to load threat model">
                  <ThreatModelTab />
                </ErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="exploit-chains" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Failed to load exploit chains">
                  <ExploitChainsTab onSwitchToThreatModel={() => setTab('threat-model')} />
                </ErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="finding-overlays" tabIndex={-1}>
                <ErrorBoundary fallbackTitle="Failed to load scan prioritization">
                  <FindingOverlaysTab onSwitchToThreatModel={() => setTab('threat-model')} />
                </ErrorBoundary>
              </Tabs.Content>
            </div>
          </div>
        </Tabs.Root>
      </div>
    </RepositoryActionMenuStoreProvider>
  )
}

const HeaderSection: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { data: repository } = useGetRepository({ repositoryId })
  const { data: workflows } = useGetWorkflows({ repositoryId })
  const hasWorkflows = (workflows?._embedded?.items?.length ?? 0) > 0
  const { setCurrentOverlay } = useRepositoryActionMenuStore(
    useShallow(({ setCurrentOverlay }) => ({ setCurrentOverlay }))
  )

  return (
    <section style={{ paddingTop: 0, paddingBottom: 0, margin: '0 72px' }} aria-label="Repository Header">
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'end' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
          <RepositoryHeader
            repository={repository}
            showDefaultBranchTag={true}
            hasWorkflows={hasWorkflows}
            onWorkflowClick={() => setCurrentOverlay('manage-workflows')}
            isPageTitle={true}
          />
        </div>
        <RepositoryActionMenuWithOverlays repository={repository} />
      </div>
    </section>
  )
}

const ToolSelectorWrapper = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const [searchParams] = useSearchParams()
  const isToolScoped = TOOL_SCOPED_TABS.includes(parseTab(searchParams.get('tab')))

  const { selectedTool, toggleSelectedTool } = useSelectedTool()
  const { data: tools, isLoading } = useGetToolsForRepository({ repositoryId })

  if (isLoading) {
    return <Spinner />
  }

  if (!selectedTool) {
    // Note: In practice this will only occur in two cases:
    //   1. One time immediately before the component re-renders with a non-undefined `selectedTool`. This is because
    //      when `isLoading` transitions to `false`, `useSelectToolOnLoad`'s `useEffect` will set `selectedTool`.
    //   2. When there neither any tool integrations nor any scans for the repository.
    return (
      <div className={styles.noRelevantTools}>
        <span className={styles.noRelevantToolsLabel}>No relevant tools</span>
        <CaretDownIcon className={styles.noRelevantToolsCaret} size={24} weight="fill" />
      </div>
    )
  }

  return (
    <ToolSelector
      repositoryId={repositoryId}
      selectedTool={selectedTool}
      setSelectedTool={toggleSelectedTool}
      tools={Array.from(tools)}
      isDisabled={!isToolScoped}
      disabledReason={REPOSITORY_WIDE_REASON}
    />
  )
}

const RepositoryActionMenuWithOverlays = ({ repository }: { repository?: Repository }) => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const repositoryActionProps = useRepositoryActionMenuStore(
    useShallow(({ currentOverlay, setCurrentOverlay }) => ({ currentOverlay, setCurrentOverlay }))
  )
  const { addToast } = useToastService()
  const { data: integrations } = useGetRepositoryIntegrations({ repositoryId })
  const mappedTools: Tool[] = useMemo(
    () => [
      ...new Set(
        (integrations?._embedded?.items ?? []).map(integration => convertIntegrationTypeToTool(integration.type))
      ),
    ],
    [integrations]
  )
  const showSyncError = useShowMutationError<typeof usePostUploadScan>({
    prefix: (_error, variables) =>
      `Failed to sync ${getToolName(variables.body.metadata.tool)} findings to ${repository?.name ?? 'repository'}`,
  })
  const syncToolMutation = usePostUploadScan()
  const handleSyncTool = useCallback(
    (tool: Tool) => {
      if (!repository) return
      syncToolMutation.mutate(
        { repositoryId: repository.id, body: { metadata: { tool, workflow_execution_policy: 'execute' } } },
        {
          onSuccess: () => {
            addToast({
              message: (
                <>
                  Latest {getToolName(tool)} findings synced to {repository.name}
                </>
              ),
              variant: 'success',
            })
          },
          onError: showSyncError,
        }
      )
    },
    [syncToolMutation, addToast, repository, showSyncError]
  )

  return (
    <>
      <RepositoryActionMenu {...repositoryActionProps} mappedTools={mappedTools} onSyncTool={handleSyncTool} />
      {repository && <RepositoryActionOverlays repository={repository} {...repositoryActionProps} />}
    </>
  )
}

const RepositoryDetailsPageNewerScanBanner: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { selectedTool } = useSelectedTool()
  const toolsFilter = selectedTool ? [selectedTool] : undefined

  const { data: analyzedScans } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
    tools: toolsFilter,
    analysisState: 'completed',
  })
  const { data: allScans } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
    tools: toolsFilter,
  })

  const currentScanId = analyzedScans?._embedded?.items?.[0]?.id ?? allScans?._embedded?.items?.[0]?.id

  if (!currentScanId || !selectedTool) return null

  return (
    <div style={{ marginLeft: 'auto' }}>
      <NewerScanBanner
        currentScanId={currentScanId}
        repositoryId={repositoryId}
        tool={selectedTool}
        onView={({ newerScanId, state }) => {
          if (state === 'has-analysis') {
            queryClient.invalidateQueries({ queryKey: ['scans'] })
          } else {
            navigate(Routes.ScanDetailsPage.createPath({ scanId: newerScanId }))
          }
        }}
        viewLabel="View"
      />
    </div>
  )
}

const RepositoryDetailsPageBreadcrumb: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { data: repository } = useGetRepository({ repositoryId })
  const [searchParams] = useSearchParams()
  const isToolScoped = TOOL_SCOPED_TABS.includes(parseTab(searchParams.get('tab')))
  const { selectedTool } = useSelectedTool()
  const isAntaresDemo = isAntaresDemoRepositoryId(repositoryId)
  const scopedToolName = isAntaresDemo
    ? ANTARES_DEMO_TOOL_NAME
    : isToolScoped
      ? getToolName(selectedTool ?? undefined)
      : undefined

  return (
    <RepositoryDetailsPageBreadcrumbPure
      repositoryName={repository?.name}
      defaultBranch={repository?.default_branch}
      // The suffix names what the page below is scoped to. On the repository-wide views it would
      // assert a tool scope that does not exist — the same claim the inert selector stopped making.
      toolName={isToolScoped || isAntaresDemo ? scopedToolName : undefined}
      isAntaresDemo={isAntaresDemo}
    />
  )
}

export type RepositoryDetailsPageBreadcrumbPureProps = {
  repositoryName?: string
  defaultBranch?: string
  toolName?: string
  /**
   * Demo-only: when the current repository is one of the four Antares mockup repos, replace the
   * standard "All repositories" root with "Antares scans" so viewers can return to the cross-repo
   * report at /antares-scans.
   */
  isAntaresDemo?: boolean
}

export const RepositoryDetailsPageBreadcrumbPure: React.FC<RepositoryDetailsPageBreadcrumbPureProps> = ({
  repositoryName,
  defaultBranch,
  toolName,
  isAntaresDemo = false,
}) => {
  // Built up piece by piece rather than all-or-nothing: dropping the tool suffix on the repository-wide
  // views must not take the branch down with it, which an `&&` chain over all three would.
  const repoLabel = repositoryName
    ? `${repositoryName}${defaultBranch ? ` (${defaultBranch})` : ''}${toolName ? `(${toolName})` : ''}`
    : repositoryName

  const rootItem = isAntaresDemo
    ? { label: 'Antares scans', to: Routes.AntaresScansPage.path }
    : { label: 'All repositories', to: Routes.RepositoriesPage.path }

  return <Breadcrumb items={[rootItem, { label: repoLabel ?? 'Repository details', to: '#' }]} />
}

export const useRepositoryDetailsPageParams = () => {
  const { repositoryId } = useParams()
  invariant(repositoryId, 'Missing required parameter: repositoryId')
  return useMemo(() => ({ repositoryId }), [repositoryId])
}

export function useSelectedTool() {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedTool = ToolSchema.safeParse(searchParams.get('tool')).data ?? null

  const toggleSelectedTool = useCallback(
    (tool: Tool | null) => {
      setSearchParams(
        prev => {
          if (tool) {
            prev.set('tool', tool)
          } else {
            prev.delete('tool')
          }
          return prev
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return useMemo(() => ({ selectedTool, toggleSelectedTool }), [selectedTool, toggleSelectedTool])
}

function useSelectToolOnLoad() {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { data: latestScans } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
  })
  const tool = latestScans?._embedded?.items?.[0]?.detector

  const { selectedTool, toggleSelectedTool } = useSelectedTool()
  const { data: tools } = useGetToolsForRepository({ repositoryId })

  useEffect(() => {
    if (tool && !selectedTool && tools.has(tool)) {
      toggleSelectedTool(tool)
    }
  }, [tool, selectedTool, tools, toggleSelectedTool])
}
